import { Trans, useLingui } from "@lingui/react/macro";
import { useId } from "react";

import type { WebAuthnCredentialResponse } from "../../../api";

import { usePasskeyProviders } from "../../../catalogs/passkey-providers";
import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import { Button } from "../../../components/ui-kit/button";
import { SimpleTable } from "../../../components/ui-kit/tables/simple-table";

/** The user's passkeys, each named after the provider that keeps it. */
export function PasskeyList({
  passkeys,
  onRemove,
  removalBlocked,
}: {
  passkeys: WebAuthnCredentialResponse[];
  onRemove: (passkey: WebAuthnCredentialResponse) => Promise<unknown>;
  /** Why none of them may be removed right now, if none may. */
  removalBlocked?: string;
}) {
  const { t, i18n } = useLingui();
  const idPrefix = useId();
  const providerNames = usePasskeyProviders();

  function nameOf(passkey: WebAuthnCredentialResponse) {
    const provider = passkey.aaguid ? providerNames[passkey.aaguid] : null;
    return passkey.device_name ?? provider ?? t`Passkey`;
  }

  /** The name cell, which also describes the row's Remove button. */
  function nameId(passkey: WebAuthnCredentialResponse) {
    return `${idPrefix}-${passkey.uuid}`;
  }

  return (
    <SimpleTable
      rows={passkeys}
      itemKeyField="uuid"
      columns={[
        {
          header1: <Trans>Name</Trans>,
          cell: (passkey) => (
            <span id={nameId(passkey)}>{nameOf(passkey)}</span>
          ),
        },
        {
          header1: <Trans>Added</Trans>,
          cell: (passkey) =>
            i18n.date(passkey.created_at, { dateStyle: "medium" }),
        },
        {
          header1: <Trans>Last used</Trans>,
          cell: (passkey) =>
            passkey.last_used_at ? (
              i18n.date(passkey.last_used_at, {
                dateStyle: "medium",
                timeStyle: "short",
              })
            ) : (
              <Trans>Never</Trans>
            ),
        },
        {
          header1: null,
          align: "right",
          cell: (passkey) => (
            <Tooltip content={removalBlocked}>
              <Button
                size="sm"
                destructive
                disabled={removalBlocked !== undefined}
                aria-describedby={nameId(passkey)}
                onClick={() => onRemove(passkey)}
              >
                <Trans>Remove</Trans>
              </Button>
            </Tooltip>
          ),
        },
      ]}
    />
  );
}
